import { useState, useRef, useEffect } from 'react';
import { motion, PanInfo } from 'framer-motion';

const images = [
    "/project-lehana-hybrid-vm-autonomous-infrastructure-management.png",
    "/project-meeting-master-ai-realtime-semantic-transcription-and-analysis.png",
    "/project-ecoscraper-distributed-high-performance-data-extraction.png",
    "/project-health-monitor-agentic-observability-control-center.png",
    "/project-portfolio-v1-legacy-cinematic-3d-experience.png",
    "/project-neural-dns-blockchain-secure-decentralized-identity.png",
    "/paras-lehana-portrait-visionary-architect-neural-neon.png",
    "/project-lehana-hybrid-vm-autonomous-infrastructure-management.png",
];

export function Circular3DGallery() {
    const [rotation, setRotation] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Auto-rotation speed
    const ROTATION_SPEED = 0.05;

    useEffect(() => {
        let animationFrameId: number;

        const animate = () => {
            if (!isDragging) {
                setRotation(prev => prev - ROTATION_SPEED);
            }
            animationFrameId = requestAnimationFrame(animate);
        };

        animationFrameId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrameId);
    }, [isDragging]);

    const handleDrag = (_: any, info: PanInfo) => {
        setRotation(prev => prev + info.delta.x * 0.5);
    };

    const handleNext = () => {
        setRotation(prev => prev - 45);
    };

    const handlePrev = () => {
        setRotation(prev => prev + 45);
    };

    return (
        <div
            ref={containerRef}
            className="relative h-[600px] w-full flex items-center justify-center perspective-1000 overflow-hidden cursor-grab active:cursor-grabbing"
            onPointerDown={() => setIsDragging(true)}
            onPointerUp={() => setIsDragging(false)}
            onPointerLeave={() => setIsDragging(false)}
        >
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-500/5 to-transparent pointer-events-none" />

            <motion.div
                className="relative w-64 h-80 transform-style-3d"
                style={{
                    transformStyle: "preserve-3d",
                    rotateY: rotation
                }}
                onPan={handleDrag}
            >
                {images.map((src, i) => {
                    const angle = (360 / images.length) * i;
                    // Calculate relative rotation in radians
                    const radian = ((angle + rotation) * Math.PI) / 180;
                    // Cosine gives us depth: 1 is front, -1 is back
                    const depth = Math.cos(radian);

                    // Normalize depth from -1...1 to 0...1 (0 is back, 1 is front)
                    const normalizedDepth = (depth + 1) / 2;

                    // Dynamic styles — very subtle depth effect (user requested ~5% blur)
                    const brightness = 0.7 + (0.3 * normalizedDepth); // 0.7 at back, 1.0 at front
                    const blur = (1 - normalizedDepth) * 1; // 1px blur at back, 0px at front
                    const opacity = 0.75 + (0.25 * normalizedDepth); // 0.75 at back, 1.0 at front

                    return (
                        <div
                            key={i}
                            className="absolute inset-0 w-full h-full backface-hidden border-2 border-emerald-500/30 rounded-xl overflow-hidden bg-black/80 shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all duration-75"
                            style={{
                                transform: `rotateY(${angle}deg) translateZ(400px)`,
                                filter: `brightness(${brightness}) blur(${blur}px)`,
                                opacity: opacity
                            }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
                            <img src={src} alt={`Gallery item ${i}`} className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity pointer-events-none" />
                            <div className="absolute bottom-4 left-4 font-mono text-xs text-emerald-400 backdrop-blur-sm px-2 py-1 rounded bg-black/50">ITEM_0{i + 1}</div>
                        </div>
                    );
                })}
            </motion.div>

            <div className="absolute bottom-10 flex gap-8 z-50 pointer-events-auto">
                <button onClick={handlePrev} className="px-6 py-2 bg-black/50 border border-emerald-500/30 text-emerald-400 rounded-full hover:bg-emerald-500 hover:text-black transition-all shadow-[0_0_15px_rgba(16,185,129,0.2)]">
                    Prev
                </button>
                <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="text-[10px] font-mono text-emerald-500/50 tracking-[0.2em] uppercase">Drag to Rotate</span>
                </div>
                <button onClick={handleNext} className="px-6 py-2 bg-black/50 border border-emerald-500/30 text-emerald-400 rounded-full hover:bg-emerald-500 hover:text-black transition-all shadow-[0_0_15px_rgba(16,185,129,0.2)]">
                    Next
                </button>
            </div>
        </div>
    );
}
